import {Fragment} from 'react';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon';
import {Tooltip} from '../Tooltip';
import {Typography} from '../Typography';

import type {IconName} from '../Icon';
import type {FC, ReactNode} from 'react';

export interface HeaderMetaItem {
  icon: IconName;
  /** Rich content is allowed so one item can carry more than one emphasis — e.g. a deal type
   * followed by its sub-type — without becoming a `custom` entry and losing the icon and tooltip. */
  value: ReactNode;
  tooltip: string;
}

export interface HeaderMetaCustomItem {
  /** An arbitrary, richer element interleaved with the plain icon+text items — e.g. a
   * popover-triggering control that replaces what would otherwise be several plain items. */
  custom: ReactNode;
  key: string;
}

export type HeaderMetaEntry = HeaderMetaItem | HeaderMetaCustomItem;

export interface HeaderMetaRowProps {
  items: HeaderMetaEntry[];
  /** Move whole items to the next line when the row runs out of room, instead of letting one
   * item compress and truncate mid-value. Off by default — it costs vertical space. */
  wrap?: boolean;
}

/**
 * Shared row of icon+text+tooltip meta items, used in page headers.
 * Each item is only rendered when its value is non-empty.
 */
export const HeaderMetaRow: FC<HeaderMetaRowProps> = ({items, wrap = false}) => (
  <div
    data-slot="header-meta-row"
    className={cn('flex items-center gap-x-4', wrap && 'flex-wrap gap-y-2')}
  >
    {items.map(item => {
      if ('custom' in item) return <Fragment key={item.key}>{item.custom}</Fragment>;

      const {icon, value, tooltip} = item;
      if (!value) return null;
      return (
        <Tooltip key={icon} content={tooltip} side="bottom">
          <div className={cn('flex cursor-default items-center gap-2', wrap && 'shrink-0')}>
            <Icon name={icon} width={14} height={14} className="shrink-0 text-fg-tertiary" />
            <Typography size="xs" color="secondary">
              {value}
            </Typography>
          </div>
        </Tooltip>
      );
    })}
  </div>
);
